import type { ReactNode } from 'react'
import { GatedValue } from '@/components/gate'

/**
 * How a stat tile renders a figure it does not have.
 *
 * Three states, and telling them apart matters:
 *
 *  - a real number                → render it
 *  - locked (`financialsHidden`)  → the blurred bar, sized like the real value
 *  - unavailable (a failed fetch) → an em dash
 *
 * The blur and the dash must not be swapped. A dash for a locked figure reads
 * as "we have nothing", which invites the user to conclude the market is empty.
 * A blur for a failed fetch reads as "you are not allowed", which is a lie
 * about their account.
 */
export const statValue = ({
  value,
  financialsHidden,
  label,
  widthCh,
  render,
}: {
  value: number | null
  financialsHidden: boolean
  /** For the accessible name on the blurred bar. */
  label: string
  widthCh: number
  render: (value: number) => ReactNode
}): ReactNode => {
  if (value !== null) return render(value)
  if (financialsHidden) return <GatedValue label={label} widthCh={widthCh} />
  return <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">—</span>
}
